import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Building2, Users, Calculator, TrendingUp, Landmark, PiggyBank, Plus, Trash2, Save, AlertTriangle, Briefcase, FileText, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatEur = (v) => {
  if (v == null) return "-";
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
};

const PIE_COLORS = ["#2563eb", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6", "#ec4899"];

// ==================== SRL SIMULATION SECTION ====================

const SrlSimulationSection = ({ sim, simParams, setSimParams, onSave, saving }) => {
  if (!sim) return null;
  const { confronto: c, srl, costi_aggiuntivi: ca, amministratori: admins, parametri: p } = sim;
  const isVantaggioso = c.differenza > 0;

  return (
    <Card data-testid="srl-simulation-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <ArrowRightLeft className="h-4 w-4 text-indigo-600" />
          Simulazione: E se fosse una SRL?
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Confronto tra la situazione attuale e l'ipotesi di operare come SRL con compensi amministratore.
          Modifica i parametri e premi Ricalcola.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Warning banner per simulazioni insostenibili */}
        {sim.warning && (
          <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3" data-testid="sim-warning">
            <div className="flex items-start gap-2">
              <span className="text-xl">⚠️</span>
              <div className="text-sm text-amber-900">
                <div className="font-bold mb-0.5">Attenzione: ipotesi da rivedere</div>
                <div>{sim.warning}</div>
              </div>
            </div>
          </div>
        )}

        {/* Editable Params */}
        <div className="flex flex-wrap gap-3 items-end p-3 bg-slate-50 rounded-lg">
          <div className="flex-1 min-w-[180px]">
            <Label className="text-xs font-medium">Compenso Amm.re Lordo Mensile</Label>
            <Input type="number" min="0" step="100"
              value={simParams.compenso_mensile || ''}
              onChange={e => setSimParams(prev => ({ ...prev, compenso_mensile: e.target.value }))}
              data-testid="sim-compenso-mensile" />
          </div>
          <div className="flex-1 min-w-[180px]">
            <Label className="text-xs font-medium">Rimborso Km Annuo / Amm.re</Label>
            <Input type="number" min="0" step="100"
              value={simParams.rimborso_km_annuo || ''}
              onChange={e => setSimParams(prev => ({ ...prev, rimborso_km_annuo: e.target.value }))}
              data-testid="sim-rimborso-km" />
          </div>
          <Button onClick={onSave} disabled={saving} size="sm" data-testid="sim-save-btn">
            <Save className="h-3.5 w-3.5 mr-1" />{saving ? 'Salvataggio...' : 'Ricalcola'}
          </Button>
          <div className="text-xs text-muted-foreground">
            {p.num_amministratori} amministrator{p.num_amministratori > 1 ? 'i' : 'e'}
          </div>
        </div>

        {/* Main Comparison */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* Current */}
          <div className={`border-2 rounded-lg p-4 ${!isVantaggioso ? 'border-green-400 bg-green-50/30' : 'border-slate-200'}`}>
            <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Situazione Attuale</div>
            <div className="text-2xl font-bold tabular-nums" data-testid="sim-netto-attuale">{formatEur(c.netto_attuale)}</div>
            <div className="text-xs text-muted-foreground">Netto dopo imposte e contributi</div>
          </div>
          {/* SRL */}
          <div className={`border-2 rounded-lg p-4 ${isVantaggioso ? 'border-green-400 bg-green-50/30' : 'border-slate-200'}`}>
            <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Simulazione SRL</div>
            <div className="flex items-baseline gap-2">
              <div className="text-2xl font-bold tabular-nums" data-testid="sim-netto-srl">{formatEur(c.srl_totale)}</div>
              <div className={`text-sm font-bold px-2 py-0.5 rounded-full ${isVantaggioso ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`} data-testid="sim-differenza">
                {c.differenza >= 0 ? '+' : ''}{formatEur(c.differenza)} ({c.vantaggio_pct > 0 ? '+' : ''}{c.vantaggio_pct}%)
              </div>
            </div>
            <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
              <div>Netto compensi amm.re: {formatEur(c.srl_admin_netto)}</div>
              {c.srl_dividendo_netto > 0 && (
                <div>Netto dividendi distribuiti: {formatEur(c.srl_dividendo_netto)}</div>
              )}
              <div className="text-blue-700 font-medium">Utile residuo in SRL: {formatEur(sim.parita_netto?.srl_utile_residuo || 0)}</div>
            </div>
          </div>
        </div>

        {/* SRL P&L + Costi aggiuntivi + Admin detail */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* SRL P&L */}
          <Card className="border">
            <CardHeader className="pb-1 pt-3 px-3">
              <CardTitle className="text-xs font-semibold uppercase text-muted-foreground">CE Simulato SRL</CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-3 space-y-1 text-sm">
              <div className="flex justify-between"><span>Fatturato</span><span className="tabular-nums font-medium">{formatEur(srl.fatturato)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>Costi attuali</span><span className="tabular-nums">-{formatEur(srl.costi_attuali)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>Costi aggiuntivi SRL</span><span className="tabular-nums">-{formatEur(srl.costi_aggiuntivi)}</span></div>
              <div className="flex justify-between border-t pt-1 font-medium"><span>Risultato ante imposte</span><span className={`tabular-nums ${(srl.risultato_pre_imposte ?? srl.risultato) < 0 ? 'text-red-700 font-bold' : ''}`}>{formatEur(srl.risultato_pre_imposte ?? srl.risultato)}</span></div>
              <div className="flex justify-between text-red-600 text-xs"><span>IRES (24%)</span><span className="tabular-nums">-{formatEur(srl.ires)}</span></div>
              <div className="flex justify-between text-amber-600 text-xs"><span>IRAP (3,9%)</span><span className="tabular-nums">-{formatEur(srl.irap)}</span></div>
              <div className="flex justify-between border-t pt-1 font-bold"><span>Utile netto SRL</span><span className={`tabular-nums ${srl.utile_netto < 0 ? 'text-red-700' : 'text-blue-700'}`}>{formatEur(srl.utile_netto)}</span></div>
              {srl.insostenibile && (
                <div className="text-[10px] text-red-600 italic pt-1 border-t mt-1">⚠ La società va in perdita con questi compensi</div>
              )}
            </CardContent>
          </Card>

          {/* Costi aggiuntivi */}
          <Card className="border">
            <CardHeader className="pb-1 pt-3 px-3">
              <CardTitle className="text-xs font-semibold uppercase text-muted-foreground">Costi Aggiuntivi SRL</CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-3 space-y-1 text-sm">
              <div className="flex justify-between"><span>Compensi lordi</span><span className="tabular-nums">{formatEur(ca.compensi_lordi)}</span></div>
              <div className="flex justify-between"><span>INPS quota azienda (2/3)</span><span className="tabular-nums">{formatEur(ca.inps_quota_azienda)}</span></div>
              <div className="flex justify-between"><span>Rimborsi km</span><span className="tabular-nums">{formatEur(ca.rimborsi_km)}</span></div>
              <div className="flex justify-between text-xs text-muted-foreground"><span>Elab. paghe amm.ri</span><span className="tabular-nums">{formatEur(ca.paghe_amministratori)}</span></div>
              <div className="flex justify-between text-xs text-muted-foreground"><span>Contabilita aggiuntiva</span><span className="tabular-nums">{formatEur(ca.contabilita)}</span></div>
              <div className="flex justify-between text-xs text-muted-foreground"><span>Diritti camerali</span><span className="tabular-nums">{formatEur(ca.diritti_camerali)}</span></div>
              <div className="flex justify-between border-t pt-1 font-bold"><span>Totale costi</span><span className="tabular-nums">{formatEur(ca.totale)}</span></div>
            </CardContent>
          </Card>

          {/* Admin detail */}
          <Card className="border">
            <CardHeader className="pb-1 pt-3 px-3">
              <CardTitle className="text-xs font-semibold uppercase text-muted-foreground">
                {admins.length > 1 ? 'Amministratori' : 'Amministratore'}
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-3 space-y-2 text-sm">
              {admins.map((a, i) => (
                <div key={i} className={admins.length > 1 ? "border-b pb-2 last:border-0 last:pb-0" : ""}>
                  {admins.length > 1 && <div className="font-medium text-xs text-violet-700 mb-1">{a.name}</div>}
                  <div className="flex justify-between"><span>Compenso lordo</span><span className="tabular-nums">{formatEur(a.compenso_lordo)}</span></div>
                  <div className="flex justify-between text-xs text-muted-foreground"><span>INPS (1/3 amm.re)</span><span className="tabular-nums">-{formatEur(a.inps_quota_admin)}</span></div>
                  <div className="flex justify-between text-xs text-muted-foreground"><span>IRPEF netta</span><span className="tabular-nums">-{formatEur(a.irpef_netta)}</span></div>
                  <div className="flex justify-between text-xs text-green-700"><span>Rimborso km (esentasse)</span><span className="tabular-nums">+{formatEur(a.rimborso_km)}</span></div>
                  <div className="flex justify-between font-bold border-t pt-1"><span>Netto</span><span className="tabular-nums text-green-700">{formatEur(a.netto)}</span></div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Nota */}
        <p className="text-[11px] text-muted-foreground italic px-1">
          Simulazione a scopo indicativo. Confronto a parita di netto in tasca ai soci.
          I compensi amm.re sono deducibili IRES ma indeducibili IRAP. Il rimborso km e deducibile al 100% per la SRL e non fa reddito per l'amministratore.
        </p>

        {/* === UTILE RESIDUO SRL — visibilità protagonista === */}
        {sim.parita_netto && (
          <div className="rounded-xl border-2 border-blue-300 bg-gradient-to-br from-blue-50 to-cyan-50 p-4 shadow-sm" data-testid="sim-utile-residuo-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-1">
                  Utile residuo in capo alla SRL
                </div>
                <p className="text-[12px] text-slate-600 max-w-md">
                  Dopo aver garantito ai soci lo stesso netto attuale, questo è il margine che resta
                  in azienda — disponibile per <strong>distribuzione</strong>, <strong>investimenti</strong> o <strong>accantonamenti</strong>.
                </p>
              </div>
              <div className="text-right">
                <div className={`text-4xl sm:text-5xl font-black tabular-nums ${(sim.parita_netto.srl_utile_residuo || 0) > 0 ? 'text-blue-700' : 'text-red-700'}`}>
                  {formatEur(sim.parita_netto.srl_utile_residuo || 0)}
                </div>
                {!sim.parita_netto.fattibile && (
                  <div className="text-[11px] text-red-600 font-medium italic mt-1">⚠ Compensi ipotizzati non sostenibili</div>
                )}
              </div>
            </div>
            {sim.parita_netto.fattibile && sim.parita_netto.srl_utile_residuo > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4 pt-3 border-t border-blue-200">
                <div className="bg-white rounded-md p-2 text-center">
                  <div className="text-[10px] uppercase text-slate-500 font-semibold">Se distribuisci tutto</div>
                  <div className="text-sm font-bold tabular-nums text-emerald-700">+{formatEur(sim.parita_netto.srl_utile_residuo * 0.74)} netto soci</div>
                  <div className="text-[9px] text-slate-400 italic">−26% tassazione dividendi</div>
                </div>
                <div className="bg-white rounded-md p-2 text-center">
                  <div className="text-[10px] uppercase text-slate-500 font-semibold">Se investi tutto</div>
                  <div className="text-sm font-bold tabular-nums text-violet-700">{formatEur(sim.parita_netto.srl_utile_residuo)}</div>
                  <div className="text-[9px] text-slate-400 italic">100% al lordo (poi ammortamenti)</div>
                </div>
                <div className="bg-white rounded-md p-2 text-center">
                  <div className="text-[10px] uppercase text-slate-500 font-semibold">Se accantoni</div>
                  <div className="text-sm font-bold tabular-nums text-slate-700">{formatEur(sim.parita_netto.srl_utile_residuo)}</div>
                  <div className="text-[9px] text-slate-400 italic">a riserva, senza ulteriori imposte</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* PARITA DI NETTO - Visual Story Comparison */}
        {sim.parita_netto && (
          <div className="rounded-xl overflow-hidden border-2 border-emerald-300 shadow-sm" data-testid="parita-netto-section">
            {/* Header */}
            <div className="bg-slate-800 text-white px-5 py-4">
              <div className="flex items-center gap-2">
                <Landmark className="h-5 w-5 text-emerald-400" />
                <h3 className="text-lg font-bold">A Parita di Netto</h3>
              </div>
              <p className="text-slate-300 text-sm mt-1">
                Stesso fatturato, costi piu alti ma deducibili, meno tasse, stesso netto in tasca
                {sim.parita_netto.srl_utile_residuo > 0 && (
                  <span className="text-emerald-400 font-semibold"> + utile che resta in azienda</span>
                )}
              </p>
            </div>

            <div className="bg-white">
              {/* Comparison Table */}
              <div className="overflow-hidden">
                {/* Table Header */}
                <div className="grid grid-cols-[1fr_100px_1fr] sm:grid-cols-[1fr_120px_1fr] border-b bg-slate-50">
                  <div className="px-4 py-2.5 text-center text-xs font-bold uppercase tracking-wider text-slate-500">Attuale</div>
                  <div className="px-2 py-2.5 text-center border-x"></div>
                  <div className="px-4 py-2.5 text-center text-xs font-bold uppercase tracking-wider text-slate-500">SRL</div>
                </div>

                {/* Row: Fatturato */}
                <div className="grid grid-cols-[1fr_100px_1fr] sm:grid-cols-[1fr_120px_1fr] items-center border-b">
                  <div className="px-4 py-3 text-center">
                    <div className="text-lg sm:text-xl font-bold tabular-nums">{formatEur(srl.fatturato)}</div>
                  </div>
                  <div className="px-2 py-3 border-x bg-slate-50 text-center">
                    <div className="text-[10px] sm:text-xs font-semibold uppercase text-slate-500">Fatturato</div>
                    <div className="text-[10px] sm:text-xs font-bold text-blue-600 mt-0.5">= uguale</div>
                  </div>
                  <div className="px-4 py-3 text-center">
                    <div className="text-lg sm:text-xl font-bold tabular-nums">{formatEur(srl.fatturato)}</div>
                  </div>
                </div>

                {/* Row: Costi */}
                <div className="grid grid-cols-[1fr_100px_1fr] sm:grid-cols-[1fr_120px_1fr] items-center border-b bg-amber-50/40">
                  <div className="px-4 py-3 text-center">
                    <div className="text-lg sm:text-xl font-bold tabular-nums">{formatEur(srl.costi_attuali)}</div>
                  </div>
                  <div className="px-2 py-3 border-x bg-amber-50 text-center">
                    <div className="text-[10px] sm:text-xs font-semibold uppercase text-slate-500">Costi</div>
                    <div className="text-[10px] sm:text-xs font-bold text-amber-600 mt-0.5">+{formatEur(ca.totale)}</div>
                    <div className="text-[9px] sm:text-[10px] text-amber-700 font-medium">deducibili</div>
                  </div>
                  <div className="px-4 py-3 text-center">
                    <div className="text-lg sm:text-xl font-bold tabular-nums">{formatEur(srl.costi_totali)}</div>
                  </div>
                </div>

                {/* Row: Costo Fiscale */}
                <div className="grid grid-cols-[1fr_100px_1fr] sm:grid-cols-[1fr_120px_1fr] items-center border-b bg-green-50/40">
                  <div className="px-4 py-3 text-center">
                    <div className="text-lg sm:text-xl font-bold tabular-nums text-red-600">{formatEur(sim.parita_netto.costo_fiscale_attuale)}</div>
                  </div>
                  <div className="px-2 py-3 border-x bg-green-50 text-center">
                    <div className="text-[10px] sm:text-xs font-semibold uppercase text-slate-500">Fisco</div>
                    {sim.parita_netto.risparmio > 0 ? (
                      <div className="text-xs sm:text-sm font-bold text-emerald-600 mt-0.5" data-testid="sim-risparmio-parita">-{formatEur(sim.parita_netto.risparmio)}</div>
                    ) : (
                      <div className="text-xs sm:text-sm font-bold text-red-600 mt-0.5" data-testid="sim-risparmio-parita">+{formatEur(Math.abs(sim.parita_netto.risparmio))}</div>
                    )}
                  </div>
                  <div className="px-4 py-3 text-center">
                    <div className={`text-lg sm:text-xl font-bold tabular-nums ${sim.parita_netto.risparmio > 0 ? 'text-emerald-700' : 'text-red-600'}`}>{formatEur(sim.parita_netto.costo_fiscale_srl)}</div>
                  </div>
                </div>

                {/* Row: Netto in Tasca */}
                <div className="grid grid-cols-[1fr_100px_1fr] sm:grid-cols-[1fr_120px_1fr] items-center border-b-2 border-slate-300">
                  <div className="px-4 py-3 text-center bg-blue-50/30">
                    <div className="text-lg sm:text-xl font-bold tabular-nums">{formatEur(sim.parita_netto.netto_obiettivo)}</div>
                  </div>
                  <div className="px-2 py-3 border-x bg-blue-50 text-center">
                    <div className="text-[10px] sm:text-xs font-bold uppercase text-blue-700">Netto in Tasca</div>
                    <div className="text-[10px] sm:text-xs font-bold text-blue-600 mt-0.5">= uguale</div>
                  </div>
                  <div className="px-4 py-3 text-center bg-blue-50/30">
                    <div className="text-lg sm:text-xl font-bold tabular-nums">{formatEur(sim.parita_netto.netto_obiettivo)}</div>
                  </div>
                </div>
              </div>

              <div className="p-5 space-y-4">
                {/* How you get there - explanation */}
                {sim.parita_netto.gap_da_dividendi > 0 && sim.parita_netto.fattibile && (
                  <div className="text-sm bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <span className="font-semibold text-slate-700">Come arrivi allo stesso netto:</span>{' '}
                    <span className="text-slate-600">Compenso amm.re netto </span>
                    <span className="font-bold tabular-nums">{formatEur(sim.parita_netto.admin_netto)}</span>
                    <span className="text-slate-600"> + Dividendi netti </span>
                    <span className="font-bold tabular-nums">{formatEur(sim.parita_netto.dividendo_netto)}</span>
                    <span className="text-xs text-muted-foreground ml-1">(lordo {formatEur(sim.parita_netto.dividendo_lordo)}, imposta 26%: {formatEur(sim.parita_netto.imposta_dividendi)})</span>
                  </div>
                )}

                {/* No dividends needed */}
                {sim.parita_netto.gap_da_dividendi === 0 && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800 text-center">
                    Il solo compenso amministratore gia supera il netto attuale. Non servono dividendi.
                    <br /><strong>L'intero utile SRL ({formatEur(srl.utile_netto)}) resta in azienda.</strong>
                  </div>
                )}

                {/* THE PUNCHLINE - Residual Profit */}
                {sim.parita_netto.fattibile && sim.parita_netto.srl_utile_residuo > 0 && (
                  <div className="bg-emerald-700 text-white rounded-xl p-6 text-center" data-testid="sim-utile-residuo-hero">
                    <div className="text-sm font-medium text-emerald-200 uppercase tracking-wider">In piu, la SRL trattiene</div>
                    <div className="text-3xl sm:text-4xl font-black mt-1 tracking-tight" data-testid="sim-utile-residuo">{formatEur(sim.parita_netto.srl_utile_residuo)}</div>
                    <div className="text-emerald-100 text-sm mt-2">di utile netto pronto per essere reinvestito</div>
                  </div>
                )}

                {/* Warning: not feasible */}
                {!sim.parita_netto.fattibile && sim.parita_netto.gap_da_dividendi > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                    <AlertTriangle className="h-5 w-5 text-red-500 mx-auto mb-1" />
                    <div className="text-sm font-medium text-red-800">Utile SRL insufficiente per distribuire i dividendi necessari</div>
                    <div className="text-xs text-red-600 mt-1">Prova ad aumentare il fatturato o ridurre il compenso amministratore</div>
                  </div>
                )}

                {/* Expandable detail */}
                <details className="group" data-testid="parita-netto-dettaglio">
                  <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground transition-colors select-none py-1">
                    Dettaglio costo fiscale
                  </summary>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
                    <div className="bg-slate-50 rounded-lg p-3 border">
                      <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">Costo Fiscale Attuale</div>
                      {sim.parita_netto.dettaglio_attuale?.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm py-0.5">
                          <span>{item.label}</span>
                          <span className="tabular-nums font-medium">{formatEur(item.amount)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between font-bold border-t mt-1 pt-1">
                        <span>Totale</span>
                        <span className="tabular-nums text-red-700">{formatEur(sim.parita_netto.costo_fiscale_attuale)}</span>
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 border">
                      <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">Costo Fiscale SRL</div>
                      {sim.parita_netto.dettaglio_srl?.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm py-0.5">
                          <span>{item.label}</span>
                          <span className="tabular-nums font-medium">{formatEur(item.amount)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between font-bold border-t mt-1 pt-1">
                        <span>Totale</span>
                        <span className="tabular-nums text-emerald-700">{formatEur(sim.parita_netto.costo_fiscale_srl)}</span>
                      </div>
                    </div>
                  </div>
                </details>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== PROFESSIONAL TAX UI ====================

const ProfessionalTaxView = ({ data, profInputs, setProfInputs, onSave, saving, simParams, setSimParams, onSaveSim, simSaving }) => {
  const calc = data?.calcolo;
  const isOrdinario = data?.fiscal_regime === 'ordinario';
  const regimeLabel = isOrdinario ? 'Ordinario (IRPEF)' : data?.fiscal_regime === 'forfettario_5' ? 'Forfettario 5%' : 'Forfettario 15%';

  const handleChange = (field, value) => {
    setProfInputs(prev => ({ ...prev, [field]: value === '' ? null : value }));
  };

  if (!calc) {
    return (
      <div className="space-y-6" data-testid="tax-planning-professional">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-violet-600" />
            Pianificazione Fiscale - Professionista
          </h2>
        </div>
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <p>Inserisci i compensi per calcolare le imposte.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pieData = [];
  if (calc.imposta.netta > 0) pieData.push({ name: calc.imposta.tipo, value: calc.imposta.netta });
  if (calc.contributi_dovuti > 0) pieData.push({ name: 'Contributi', value: calc.contributi_dovuti });
  if (data.net_after_tax > 0) pieData.push({ name: 'Netto', value: data.net_after_tax });

  return (
    <div className="space-y-6" data-testid="tax-planning-professional">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-violet-600" />
            Pianificazione Fiscale - Professionista
          </h2>
          <p className="text-sm text-muted-foreground">
            Regime: <span className="font-medium text-foreground">{regimeLabel}</span>
            {" — "}{data.pension_fund_label} ({data.pension_rate}%)
            {data.months_loaded > 0
              ? ` — Proiezione su ${data.months_loaded} mesi (x${data.projection_factor})`
              : ' — Inserimento manuale'}
            {" — "}Imposte {data.tax_year}, pagamenti {data.payment_year}
          </p>
        </div>
        <Button onClick={onSave} disabled={saving} data-testid="save-prof-inputs-btn">
          <Save className="h-4 w-4 mr-1" />{saving ? 'Salvataggio...' : 'Salva e Ricalcola'}
        </Button>
      </div>

      {/* INPUT FORM */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4 text-violet-600" />Dati di Input
          </CardTitle>
          <p className="text-xs text-muted-foreground">Modifica i valori e premi "Salva e Ricalcola" per aggiornare il calcolo</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Compensi */}
            <div>
              <Label className="text-xs font-medium">Compensi Lordi Annuali</Label>
              <Input
                type="number" min="0" step="100"
                value={profInputs.compensi_override ?? ''}
                onChange={e => handleChange('compensi_override', e.target.value)}
                placeholder={data.projected_compensi > 0 ? `Proiettato: ${Math.round(data.projected_compensi)}` : 'Inserisci compensi'}
                data-testid="prof-compensi"
              />
              {profInputs.compensi_override == null && data.projected_compensi > 0 && (
                <p className="text-[11px] text-blue-600 mt-0.5">Usando valore proiettato: {formatEur(data.projected_compensi)}</p>
              )}
            </div>

            {/* Costi (only ordinario) */}
            {isOrdinario && (
              <div>
                <Label className="text-xs font-medium">Costi Deducibili Annuali</Label>
                <Input
                  type="number" min="0" step="100"
                  value={profInputs.costi_override ?? ''}
                  onChange={e => handleChange('costi_override', e.target.value)}
                  placeholder={data.projected_costi > 0 ? `Proiettato: ${Math.round(data.projected_costi)}` : 'Inserisci costi'}
                  data-testid="prof-costi"
                />
                {profInputs.costi_override == null && data.projected_costi > 0 && (
                  <p className="text-[11px] text-blue-600 mt-0.5">Usando valore proiettato: {formatEur(data.projected_costi)}</p>
                )}
              </div>
            )}

            {/* Ritenute d'acconto (only ordinario) */}
            {isOrdinario && (
              <div>
                <Label className="text-xs font-medium">Ritenute d'Acconto Subite</Label>
                <Input
                  type="number" min="0" step="100"
                  value={profInputs.ritenute_acconto || ''}
                  onChange={e => setProfInputs(p => ({ ...p, ritenute_acconto: parseFloat(e.target.value) || 0 }))}
                  placeholder="0"
                  data-testid="prof-ritenute"
                />
                <p className="text-[11px] text-muted-foreground mt-0.5">20% trattenuto dai clienti sulle fatture</p>
              </div>
            )}

            {/* Contributi versati deducibili */}
            <div>
              <Label className="text-xs font-medium">Contributi Versati (Deducibili)</Label>
              <Input
                type="number" min="0" step="100"
                value={profInputs.contributi_versati_deducibili || ''}
                onChange={e => setProfInputs(p => ({ ...p, contributi_versati_deducibili: parseFloat(e.target.value) || 0 }))}
                placeholder="0"
                data-testid="prof-contributi-versati"
              />
              <p className="text-[11px] text-muted-foreground mt-0.5">Contributi previdenziali gia versati nell'anno</p>
            </div>

            {/* Acconti imposta */}
            <div>
              <Label className="text-xs font-medium">Acconti {calc.imposta.tipo} Versati</Label>
              <Input
                type="number" min="0" step="100"
                value={profInputs.acconti_imposta || ''}
                onChange={e => setProfInputs(p => ({ ...p, acconti_imposta: parseFloat(e.target.value) || 0 }))}
                placeholder="0"
                data-testid="prof-acconti-imposta"
              />
              <p className="text-[11px] text-muted-foreground mt-0.5">Acconti di imposta gia versati</p>
            </div>

            {/* Acconti contributi */}
            <div>
              <Label className="text-xs font-medium">Acconti Contributi Versati</Label>
              <Input
                type="number" min="0" step="100"
                value={profInputs.acconti_contributi || ''}
                onChange={e => setProfInputs(p => ({ ...p, acconti_contributi: parseFloat(e.target.value) || 0 }))}
                placeholder="0"
                data-testid="prof-acconti-contributi"
              />
              <p className="text-[11px] text-muted-foreground mt-0.5">Acconti previdenziali gia versati</p>
            </div>

            {/* Detrazioni anno precedente (solo ordinario) */}
            {isOrdinario && (
              <div>
                <Label className="text-xs font-medium">Detrazioni Anno Precedente</Label>
                <Input
                  type="number" min="0" step="100"
                  value={profInputs.detrazioni_precedenti || ''}
                  onChange={e => setProfInputs(p => ({ ...p, detrazioni_precedenti: parseFloat(e.target.value) || 0 }))}
                  placeholder="0"
                  data-testid="prof-detrazioni-precedenti"
                />
                <p className="text-[11px] text-muted-foreground mt-0.5">Detrazioni dell'anno prima (spese mediche, ecc.) stimate in linea</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* SUMMARY CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-3 pb-2">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Compensi</div>
            <div className="text-xl font-bold tabular-nums mt-1" data-testid="prof-compensi-total">{formatEur(calc.compensi)}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-3 pb-2">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Reddito Imponibile</div>
            <div className="text-xl font-bold tabular-nums mt-1" data-testid="prof-reddito">{formatEur(calc.reddito_imponibile)}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-3 pb-2">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Carico Fiscale</div>
            <div className="text-xl font-bold text-red-600 tabular-nums mt-1" data-testid="prof-tax-total">{formatEur(data.total_tax_burden)}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-600">
          <CardContent className="pt-3 pb-2">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Netto Stimato</div>
            <div className={`text-xl font-bold tabular-nums mt-1 ${data.net_after_tax < 0 ? 'text-red-600' : 'text-green-700'}`} data-testid="prof-net">{formatEur(data.net_after_tax)}</div>
          </CardContent>
        </Card>
      </div>

      {/* CALCULATION DETAIL + PIE */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Calculation Breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Calculator className="h-4 w-4 text-violet-600" />Dettaglio Calcolo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              {/* Step-by-step */}
              <div className="flex justify-between py-1.5 border-b">
                <span className="text-muted-foreground">Compensi lordi</span>
                <span className="font-medium tabular-nums">{formatEur(calc.compensi)}</span>
              </div>

              {isOrdinario ? (
                <>
                  <div className="flex justify-between py-1.5 border-b">
                    <span className="text-muted-foreground">(-) Costi deducibili</span>
                    <span className="font-medium tabular-nums text-red-600">- {formatEur(calc.costi)}</span>
                  </div>
                  <div className="flex justify-between py-1.5 border-b bg-slate-50/50 px-2 rounded">
                    <span className="font-medium">= Reddito professionale</span>
                    <span className="font-bold tabular-nums">{formatEur(calc.reddito_professionale)}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between py-1.5 border-b">
                    <span className="text-muted-foreground">Coefficiente redditivita</span>
                    <span className="font-medium">{calc.coefficiente_redditivita}%</span>
                  </div>
                  <div className="flex justify-between py-1.5 border-b bg-slate-50/50 px-2 rounded">
                    <span className="font-medium">= Reddito lordo</span>
                    <span className="font-bold tabular-nums">{formatEur(calc.reddito_lordo)}</span>
                  </div>
                </>
              )}

              <div className="flex justify-between py-1.5 border-b">
                <span className="text-muted-foreground">(-) Contributi versati deducibili</span>
                <span className="font-medium tabular-nums text-red-600">- {formatEur(calc.contributi_versati_deducibili)}</span>
              </div>

              <div className="flex justify-between py-1.5 border-b bg-blue-50/50 px-2 rounded">
                <span className="font-semibold">= Reddito imponibile</span>
                <span className="font-bold tabular-nums">{formatEur(calc.reddito_imponibile)}</span>
              </div>

              {/* Imposta */}
              <div className="mt-3 pt-2 border-t">
                <div className="flex justify-between py-1.5">
                  <div>
                    <span className="font-medium">{calc.imposta.tipo} lorda</span>
                    {!isOrdinario && <span className="text-xs text-muted-foreground ml-1">({calc.imposta.aliquota_pct}%)</span>}
                  </div>
                  <span className="font-bold tabular-nums">{formatEur(calc.imposta.lorda)}</span>
                </div>
              </div>

              {/* IRPEF Brackets for Ordinario */}
              {isOrdinario && calc.imposta.scaglioni?.length > 0 && (
                <div className="ml-2 p-2 bg-slate-50 rounded-lg space-y-0.5">
                  <div className="text-xs font-medium text-muted-foreground mb-1">Scaglioni IRPEF 2025</div>
                  {calc.imposta.scaglioni.map((b, i) => (
                    <div key={i} className="flex justify-between text-xs">
                      <span>{formatEur(b.from)} → {formatEur(b.to)}</span>
                      <span className="text-muted-foreground">{b.rate}%</span>
                      <span className="font-medium tabular-nums">{formatEur(b.tax)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Detrazioni (only ordinario) */}
              {isOrdinario && (calc.imposta.detrazione_lavoro_autonomo > 0 || calc.imposta.detrazioni_precedenti > 0) && (
                <div className="space-y-1 ml-2 p-2 bg-green-50/50 rounded-lg">
                  <div className="text-xs font-medium text-green-700 mb-1">Detrazioni</div>
                  {calc.imposta.detrazione_lavoro_autonomo > 0 && (
                    <div className="flex justify-between text-xs">
                      <span>Detrazione lavoro autonomo (art. 13 TUIR)</span>
                      <span className="font-medium text-green-700 tabular-nums">- {formatEur(calc.imposta.detrazione_lavoro_autonomo)}</span>
                    </div>
                  )}
                  {calc.imposta.detrazioni_precedenti > 0 && (
                    <div className="flex justify-between text-xs">
                      <span>Detrazioni anno precedente</span>
                      <span className="font-medium text-green-700 tabular-nums">- {formatEur(calc.imposta.detrazioni_precedenti)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs font-medium pt-1 border-t border-green-200">
                    <span>Totale detrazioni</span>
                    <span className="text-green-700 tabular-nums">- {formatEur(calc.imposta.detrazioni_totali)}</span>
                  </div>
                </div>
              )}

              {isOrdinario && calc.imposta.detrazione_lavoro_autonomo === 0 && !calc.imposta.detrazioni_precedenti && (
                <div className="text-xs text-muted-foreground ml-2 py-1 italic">
                  Detrazione lavoro autonomo: non spettante (reddito &gt; 50.000 EUR)
                </div>
              )}

              {/* IRPEF netta (only shown for ordinario when there are detrazioni) */}
              {isOrdinario && calc.imposta.detrazioni_totali > 0 && (
                <div className="flex justify-between py-1.5 px-2 bg-red-50/50 rounded font-medium">
                  <span>{calc.imposta.tipo} netta</span>
                  <span className="font-bold tabular-nums text-red-600">{formatEur(calc.imposta.netta)}</span>
                </div>
              )}

              {isOrdinario && (
                <div className="text-xs text-muted-foreground text-right">
                  Aliquota effettiva IRPEF: {calc.imposta.aliquota_effettiva}%
                </div>
              )}

              {/* Contributi dovuti */}
              <div className="flex justify-between py-1.5 border-b">
                <div>
                  <span className="font-medium">Contributi dovuti</span>
                  <span className="text-xs text-muted-foreground ml-1">({data.pension_fund_label} {data.pension_rate}%)</span>
                </div>
                <span className="font-bold tabular-nums text-purple-600">{formatEur(calc.contributi_dovuti)}</span>
              </div>

              {/* Saldi */}
              <div className="mt-3 pt-2 border-t space-y-1.5">
                <div className="text-xs font-medium text-muted-foreground uppercase">Saldi</div>
                {isOrdinario && (
                  <div className="flex justify-between py-1 text-xs">
                    <span>Ritenute d'acconto subite</span>
                    <span className="tabular-nums text-green-700">- {formatEur(calc.ritenute_acconto)}</span>
                  </div>
                )}
                <div className="flex justify-between py-1 text-xs">
                  <span>Acconti {calc.imposta.tipo} versati</span>
                  <span className="tabular-nums text-green-700">- {formatEur(calc.acconti_imposta)}</span>
                </div>
                <div className={`flex justify-between py-1.5 px-2 rounded font-medium ${calc.saldo_imposta >= 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                  <span>Saldo {calc.imposta.tipo}</span>
                  <span className="tabular-nums" data-testid="prof-saldo-imposta">
                    {calc.saldo_imposta >= 0 ? formatEur(calc.saldo_imposta) : `Credito ${formatEur(Math.abs(calc.saldo_imposta))}`}
                  </span>
                </div>

                <div className="flex justify-between py-1 text-xs">
                  <span>Acconti contributi versati</span>
                  <span className="tabular-nums text-green-700">- {formatEur(calc.acconti_contributi)}</span>
                </div>
                <div className={`flex justify-between py-1.5 px-2 rounded font-medium ${calc.saldo_contributi >= 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                  <span>Saldo Contributi</span>
                  <span className="tabular-nums" data-testid="prof-saldo-contributi">
                    {calc.saldo_contributi >= 0 ? formatEur(calc.saldo_contributi) : `Credito ${formatEur(Math.abs(calc.saldo_contributi))}`}
                  </span>
                </div>
              </div>

              {/* Total */}
              <div className="flex justify-between items-center py-2 mt-2 bg-slate-100 px-3 rounded-lg">
                <span className="font-semibold">Carico Fiscale Totale</span>
                <span className="font-bold text-lg text-red-700 tabular-nums">{formatEur(data.total_tax_burden)}</span>
              </div>
              {calc.compensi > 0 && (
                <div className="text-xs text-muted-foreground text-center">
                  Aliquota effettiva complessiva: {((data.total_tax_burden / calc.compensi) * 100).toFixed(1)}%
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pie Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <PiggyBank className="h-4 w-4 text-emerald-600" />Distribuzione
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={100} dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => formatEur(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[280px] text-muted-foreground text-sm">Inserisci i compensi per vedere la distribuzione</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* F24 SCHEDULE */}
      {data.f24_schedule && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4 text-amber-600" />Scadenzario F24 - Pagamenti {data.payment_year}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Imposte sull'attivita {data.tax_year} — Saldi + Acconti {data.tax_year + 1} da versare nel {data.payment_year}
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* GIUGNO */}
              <div className="border rounded-lg p-4 bg-amber-50/40">
                <div className="font-semibold text-amber-800 mb-3 flex items-center gap-1">
                  <span className="text-lg">30 Giugno</span>
                  <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full ml-auto">Saldo + 1° Acconto</span>
                </div>
                {data.f24_schedule.giugno.detail.map((d, i) => (
                  <div key={i} className="flex justify-between text-sm py-1 border-b border-amber-100 last:border-0">
                    <span>{d.label}</span>
                    <span className="font-medium tabular-nums">{formatEur(d.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between font-bold text-amber-900 border-t border-amber-300 pt-2 mt-2">
                  <span>Totale Giugno</span>
                  <span className="tabular-nums" data-testid="prof-f24-giugno">{formatEur(data.f24_schedule.giugno.total)}</span>
                </div>
              </div>

              {/* NOVEMBRE */}
              <div className="border rounded-lg p-4 bg-red-50/40">
                <div className="font-semibold text-red-800 mb-3 flex items-center gap-1">
                  <span className="text-lg">30 Novembre</span>
                  <span className="text-xs bg-red-200 text-red-800 px-2 py-0.5 rounded-full ml-auto">2° Acconto</span>
                </div>
                {data.f24_schedule.novembre.detail.map((d, i) => (
                  <div key={i} className="flex justify-between text-sm py-1 border-b border-red-100 last:border-0">
                    <span>{d.label}</span>
                    <span className="font-medium tabular-nums">{formatEur(d.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between font-bold text-red-900 border-t border-red-300 pt-2 mt-2">
                  <span>Totale Novembre</span>
                  <span className="tabular-nums" data-testid="prof-f24-novembre">{formatEur(data.f24_schedule.novembre.total)}</span>
                </div>
              </div>
            </div>

            {/* Totale annuo F24 */}
            <div className="mt-3 p-3 bg-slate-100 rounded-lg flex justify-between items-center">
              <span className="font-semibold">Totale versamenti F24 nel {data.payment_year}</span>
              <span className="font-bold text-lg tabular-nums text-slate-800" data-testid="prof-f24-annual">
                {formatEur((data.f24_schedule.giugno.total || 0) + (data.f24_schedule.novembre.total || 0))}
              </span>
            </div>

            {/* Credits */}
            {data.crediti && Object.keys(data.crediti).length > 0 && (
              <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="font-medium text-green-800 text-sm mb-1">Crediti a tuo favore</div>
                {data.crediti.imposta && (
                  <div className="flex justify-between text-sm">
                    <span>Credito {calc.imposta.tipo}</span>
                    <span className="font-medium text-green-700 tabular-nums">{formatEur(data.crediti.imposta)}</span>
                  </div>
                )}
                {data.crediti.contributi && (
                  <div className="flex justify-between text-sm">
                    <span>Credito Contributi</span>
                    <span className="font-medium text-green-700 tabular-nums">{formatEur(data.crediti.contributi)}</span>
                  </div>
                )}
                <p className="text-xs text-green-600 mt-1">Utilizzabili in compensazione nei prossimi F24</p>
              </div>
            )}

            {/* RATEIZZAZIONE */}
            {data.f24_schedule.rateizzazione?.available && (
              <div className="mt-4 border rounded-lg p-4 bg-blue-50/30">
                <div className="font-semibold text-blue-800 mb-2">
                  Rateizzazione Giugno ({data.f24_schedule.rateizzazione.num_installments} rate Giu-Nov)
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {data.f24_schedule.rateizzazione.installments.map((inst, i) => (
                    <div key={i} className="text-center border rounded p-2 bg-white">
                      <div className="text-xs text-muted-foreground">{inst.month_name}</div>
                      <div className="font-bold text-sm tabular-nums">{formatEur(inst.total)}</div>
                      {inst.interest > 0 && <div className="text-[10px] text-muted-foreground">+{formatEur(inst.interest)} int.</div>}
                    </div>
                  ))}
                </div>
                <div className="text-xs text-muted-foreground mt-2 text-center">
                  Totale con interessi: {formatEur(data.f24_schedule.rateizzazione.total_with_interest)} (interessi: {formatEur(data.f24_schedule.rateizzazione.total_interest)})
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Monthly Trend (if data loaded) */}
      {data.monthly_data?.length > 1 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-indigo-600" />Andamento Mensile Compensi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.monthly_data.map(m => ({
                name: ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"][m.month - 1],
                Compensi: Math.round(m.compensi),
                Reddito: Math.round(m.reddito),
              }))}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => formatEur(v)} />
                <Bar dataKey="Compensi" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Compensi" />
                <Bar dataKey="Reddito" fill="#10b981" radius={[4, 4, 0, 0]} name="Reddito" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* SRL Simulation */}
      <SrlSimulationSection sim={data.simulazione_srl} simParams={simParams} setSimParams={setSimParams} onSave={onSaveSim} saving={simSaving} />
    </div>
  );
};


// ==================== MAIN COMPONENT ====================

const TaxPlanningPage = ({ clientId, year }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [showPartners, setShowPartners] = useState(false);
  const [saving, setSaving] = useState(false);
  const [advances, setAdvances] = useState({});
  const [showAdvances, setShowAdvances] = useState(false);
  const [profInputs, setProfInputs] = useState({
    compensi_override: null,
    costi_override: null,
    ritenute_acconto: 0,
    contributi_versati_deducibili: 0,
    acconti_imposta: 0,
    acconti_contributi: 0,
    detrazioni_precedenti: 0,
  });
  const [simParams, setSimParams] = useState({ compenso_mensile: 2500, rimborso_km_annuo: 9000 });
  const [simSaving, setSimSaving] = useState(false);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [taxRes, partnersRes, advRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}/tax-planning?year=${year}`, { headers }),
        axios.get(`${API}/clients/${clientId}/tax-partners`, { headers }),
        axios.get(`${API}/clients/${clientId}/tax-advances?year=${year}`, { headers }),
      ]);
      setData(taxRes.data);

      // Sim params from any response type
      if (taxRes.data.simulazione_srl?.parametri) {
        setSimParams(taxRes.data.simulazione_srl.parametri);
      }

      // Professional inputs
      if (taxRes.data.business_type === 'professionista' && taxRes.data.inputs) {
        const inp = taxRes.data.inputs;
        setProfInputs({
          compensi_override: inp.compensi_override,
          costi_override: inp.costi_override,
          ritenute_acconto: inp.ritenute_acconto || 0,
          contributi_versati_deducibili: inp.contributi_versati_deducibili || 0,
          acconti_imposta: inp.acconti_imposta || 0,
          acconti_contributi: inp.acconti_contributi || 0,
          detrazioni_precedenti: inp.detrazioni_precedenti || 0,
        });
      }

      if (partnersRes.data.length > 0) {
        setPartners(partnersRes.data.map(p => ({ ...p })));
      } else if (taxRes.data.partners?.length > 0) {
        setPartners(taxRes.data.partners.map(p => ({
          name: p.name, share_pct: p.share_pct, other_income: p.other_income || 0, deductions: p.deductions || 0,
        })));
      }
      const advMap = {};
      for (const a of advRes.data) {
        const key = a.partner_name ? `${a.partner_name}_${a.advance_type}` : a.advance_type;
        advMap[key] = a.amount;
      }
      setAdvances(advMap);
    } catch {
      toast.error("Errore caricamento pianificazione fiscale");
    } finally {
      setLoading(false);
    }
  }, [clientId, year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveProfInputs = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/clients/${clientId}/professional-tax-inputs`, {
        year,
        compensi_override: profInputs.compensi_override != null ? parseFloat(profInputs.compensi_override) || 0 : null,
        costi_override: profInputs.costi_override != null ? parseFloat(profInputs.costi_override) || 0 : null,
        ritenute_acconto: parseFloat(profInputs.ritenute_acconto) || 0,
        contributi_versati_deducibili: parseFloat(profInputs.contributi_versati_deducibili) || 0,
        acconti_imposta: parseFloat(profInputs.acconti_imposta) || 0,
        acconti_contributi: parseFloat(profInputs.acconti_contributi) || 0,
        detrazioni_precedenti: parseFloat(profInputs.detrazioni_precedenti) || 0,
      }, { headers });
      toast.success("Dati salvati - ricalcolo in corso...");
      fetchData();
    } catch {
      toast.error("Errore salvataggio dati professionista");
    } finally {
      setSaving(false);
    }
  };

  const saveSimParams = async () => {
    setSimSaving(true);
    try {
      await axios.post(`${API}/clients/${clientId}/srl-simulation-params`, {
        year,
        compenso_mensile: parseFloat(simParams.compenso_mensile) || 2500,
        rimborso_km_annuo: parseFloat(simParams.rimborso_km_annuo) || 9000,
      }, { headers });
      toast.success("Parametri aggiornati - ricalcolo in corso...");
      fetchData();
    } catch {
      toast.error("Errore salvataggio parametri simulazione");
    } finally {
      setSimSaving(false);
    }
  };

  const savePartners = async () => {
    setSaving(true);
    try {
      const totalPct = partners.reduce((s, p) => s + (parseFloat(p.share_pct) || 0), 0);
      if (Math.abs(totalPct - 100) > 0.1) {
        toast.error(`Le quote devono sommare 100% (attuale: ${totalPct}%)`);
        setSaving(false);
        return;
      }
      await axios.post(`${API}/clients/${clientId}/tax-partners`, partners, { headers });
      toast.success("Soci salvati");
      setShowPartners(false);
      fetchData();
    } catch {
      toast.error("Errore salvataggio soci");
    } finally {
      setSaving(false);
    }
  };

  const saveAdvances = async () => {
    setSaving(true);
    try {
      const advList = Object.entries(advances)
        .filter(([, v]) => v > 0)
        .map(([key, amount]) => {
          const parts = key.split('_');
          if (parts.length >= 2 && (parts[parts.length - 1] === 'irpef' || parts[parts.length - 1] === 'ivs')) {
            return { type: parts[parts.length - 1], partner_name: parts.slice(0, -1).join('_'), amount };
          }
          return { type: key, amount };
        });
      await axios.post(`${API}/clients/${clientId}/tax-advances`, { year, advances: advList }, { headers });
      toast.success("Acconti salvati");
      setShowAdvances(false);
      fetchData();
    } catch {
      toast.error("Errore salvataggio acconti");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12 text-muted-foreground">Calcolo imposte previsionali...</div>;
  if (!data || data.error) return (
    <div className="flex flex-col items-center py-12 text-muted-foreground gap-2">
      <AlertTriangle className="h-8 w-8" />
      <p>{data?.error || "Nessun dato disponibile"}</p>
    </div>
  );

  // ==================== PROFESSIONAL RENDERING ====================
  if (data.business_type === 'professionista') {
    return (
      <ProfessionalTaxView
        data={data}
        profInputs={profInputs}
        setProfInputs={setProfInputs}
        onSave={saveProfInputs}
        saving={saving}
        simParams={simParams}
        setSimParams={setSimParams}
        onSaveSim={saveSimParams}
        simSaving={simSaving}
      />
    );
  }

  // ==================== CORPORATE / PERSONAL RENDERING ====================
  const isPersonal = data.business_type === "snc_sas" || data.business_type === "ditta_individuale";
  const typeLabel = data.business_type === "srl" ? "SRL" : data.business_type === "snc_sas" ? "SNC/SAS" : "Ditta Individuale";

  const pieData = [];
  if (isPersonal) {
    if (data.taxes.total_irpef > 0) pieData.push({ name: "IRPEF", value: data.taxes.total_irpef });
    if (data.taxes.total_ivs > 0) pieData.push({ name: "IVS", value: data.taxes.total_ivs });
  } else {
    if (data.taxes.amount > 0) pieData.push({ name: "IRES", value: data.taxes.amount });
  }
  if (data.irap.amount > 0) pieData.push({ name: "IRAP", value: data.irap.amount });
  const netAfterTax = data.net_result_after_tax || 0;
  if (netAfterTax > 0) pieData.push({ name: "Utile Netto", value: netAfterTax });

  const barData = (data.monthly_data || []).map(m => ({
    name: ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"][m.month - 1],
    Risultato: Math.round(m.result_before_tax),
    EBITDA: Math.round(m.ebitda),
  }));

  return (
    <div className="space-y-6" data-testid="tax-planning-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Calculator className="h-5 w-5 text-blue-600" />
            Pianificazione Fiscale - Utile {year}
          </h2>
          <p className="text-sm text-muted-foreground">
            Imposte calcolate sull'utile {year} — Scadenze F24 nel {year + 1}
            {" — "}Proiezione su {data.months_loaded} mesi (x{data.projection_factor}) — {typeLabel}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowAdvances(true)} data-testid="manage-advances-btn">
            <PiggyBank className="h-4 w-4 mr-1" />Acconti Versati
          </Button>
          {isPersonal && (
            <Button variant="outline" size="sm" onClick={() => setShowPartners(true)} data-testid="manage-partners-btn">
              <Users className="h-4 w-4 mr-1" />Gestisci Soci
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-3 pb-2">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Fatturato Proiettato</div>
            <div className="text-xl font-bold tabular-nums mt-1" data-testid="projected-revenue">{formatEur(data.annual_projected.revenue)}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-3 pb-2">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Risultato Ante Imposte</div>
            <div className="text-xl font-bold tabular-nums mt-1" data-testid="projected-result">{formatEur(data.annual_projected.result_before_tax)}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-3 pb-2">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Carico Fiscale Totale</div>
            <div className="text-xl font-bold text-red-600 tabular-nums mt-1" data-testid="total-tax">{formatEur(isPersonal && data.total_dichiarazione_2025 !== undefined ? data.total_dichiarazione_2025 : data.total_tax_burden)}</div>
            {isPersonal && data.taxes?.total_ivs_minimale > 0 && (
              <div className="text-[10px] text-muted-foreground mt-1 leading-tight">
                escluso minimale IVS {formatEur(data.taxes.total_ivs_minimale)} già versato a rate
              </div>
            )}
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-600">
          <CardContent className="pt-3 pb-2">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Utile Netto Dopo Imposte</div>
            <div className={`text-xl font-bold tabular-nums mt-1 ${netAfterTax < 0 ? 'text-red-600' : 'text-green-700'}`} data-testid="net-after-tax">{formatEur(netAfterTax)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tax Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Landmark className="h-4 w-4 text-blue-600" />Dettaglio Imposte
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.business_type === "srl" ? (
                <div className="flex justify-between items-center py-2 border-b">
                  <div>
                    <div className="font-medium">IRES</div>
                    <div className="text-xs text-muted-foreground">Base imponibile: {formatEur(data.taxes.base)} × {data.taxes.rate}%</div>
                  </div>
                  <div className="font-bold text-red-600 tabular-nums">{formatEur(data.taxes.amount)}</div>
                </div>
              ) : (
                <div className="flex justify-between items-center py-2 border-b">
                  <div>
                    <div className="font-medium">IRPEF Totale Soci</div>
                    <div className="text-xs text-muted-foreground">{data.partners?.length || 0} soci</div>
                  </div>
                  <div className="font-bold text-red-600 tabular-nums">{formatEur(data.taxes.total_irpef)}</div>
                </div>
              )}
              {data.irap.amount > 0 && (
                <div className="flex justify-between items-center py-2 border-b">
                  <div>
                    <div className="font-medium">IRAP</div>
                    <div className="text-xs text-muted-foreground">Base: {formatEur(data.irap.base)} × {data.irap.rate}%</div>
                  </div>
                  <div className="font-bold text-amber-600 tabular-nums">{formatEur(data.irap.amount)}</div>
                </div>
              )}
              {isPersonal && data.taxes.total_ivs > 0 && (
                <div className="flex justify-between items-center py-2 border-b">
                  <div>
                    <div className="font-medium">Contributi IVS — Eccedenza dichiarazione</div>
                    <div className="text-xs text-muted-foreground">
                      Minimale annuo {formatEur(data.taxes.total_ivs_minimale || 0)} versato a rate trimestrali (escluso da F24 dichiarazione)
                    </div>
                  </div>
                  <div className="font-bold text-purple-600 tabular-nums">{formatEur(data.taxes.total_ivs_variabile || 0)}</div>
                </div>
              )}
              <div className="flex justify-between items-center py-2 bg-slate-50 px-3 rounded-lg">
                <div>
                  <div className="font-semibold">Totale Carico Fiscale</div>
                  {isPersonal && data.taxes.total_ivs_minimale > 0 && (
                    <div className="text-[11px] text-muted-foreground">scaturente dalla dichiarazione 2025</div>
                  )}
                </div>
                <div className="font-bold text-lg text-red-700 tabular-nums">{formatEur(isPersonal && data.total_dichiarazione_2025 !== undefined ? data.total_dichiarazione_2025 : data.total_tax_burden)}</div>
              </div>
              {data.annual_projected.result_before_tax > 0 && (
                <div className="text-xs text-muted-foreground text-center">
                  Aliquota effettiva: {(((isPersonal && data.total_dichiarazione_2025 !== undefined ? data.total_dichiarazione_2025 : data.total_tax_burden) / data.annual_projected.result_before_tax) * 100).toFixed(1)}%
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <PiggyBank className="h-4 w-4 text-emerald-600" />Distribuzione
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => formatEur(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-muted-foreground text-sm">Nessun dato da visualizzare</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* F24 SCHEDULE */}
      {data.f24_schedule && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4 text-amber-600" />Scadenzario F24 - Pagamenti {year + 1}
            </CardTitle>
            <p className="text-xs text-muted-foreground">Imposte sull'utile {year} da versare nel {year + 1}</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="border rounded-lg p-4 bg-amber-50/40">
                <div className="font-semibold text-amber-800 mb-2 flex items-center gap-1">
                  <span className="text-lg">{data.f24_schedule.company.july.month_label || (data.f24_schedule.first_acconto_month || 'Luglio')}</span>
                  <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full ml-auto">Saldo + 1° Acconto</span>
                </div>
                {data.f24_schedule.company.july.detail.length > 0 && (
                  <div className="mb-2">
                    <div className="text-xs text-muted-foreground uppercase font-medium mb-1">Societa</div>
                    {data.f24_schedule.company.july.detail.map((d, i) => (
                      <div key={i} className="flex justify-between text-sm py-0.5">
                        <span>{d.label}</span>
                        <span className="font-medium tabular-nums">{formatEur(d.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {(data.f24_schedule.partners || []).map((p, pi) => (
                  <div key={pi} className="mb-2">
                    <div className="text-xs text-muted-foreground uppercase font-medium mb-1">
                      {p.name}
                      {p.ivs_credit > 0 && (
                        <span className="ml-2 normal-case bg-emerald-100 text-emerald-800 text-[10px] px-1.5 py-0.5 rounded" data-testid={`f24-ivs-credit-${pi}`}>
                          credito IVS {formatEur(p.ivs_credit)}
                        </span>
                      )}
                    </div>
                    {p.july.detail.map((d, i) => (
                      <div key={i} className="flex justify-between text-sm py-0.5">
                        <span>{d.label}</span>
                        <span className="font-medium tabular-nums">{formatEur(d.amount)}</span>
                      </div>
                    ))}
                  </div>
                ))}
                <div className="flex justify-between font-bold text-amber-900 border-t pt-1 mt-1">
                  <span>Totale {data.f24_schedule.company.july.month_label || (data.f24_schedule.first_acconto_month || 'Luglio')}</span>
                  <span className="tabular-nums" data-testid="f24-july-total">
                    {formatEur((data.f24_schedule.company.july.total || 0) + (data.f24_schedule.partners || []).reduce((s, p) => s + p.july.total, 0))}
                  </span>
                </div>
              </div>

              <div className="border rounded-lg p-4 bg-red-50/40">
                <div className="font-semibold text-red-800 mb-2 flex items-center gap-1">
                  <span className="text-lg">Novembre</span>
                  <span className="text-xs bg-red-200 text-red-800 px-2 py-0.5 rounded-full ml-auto">2° Acconto</span>
                </div>
                {data.f24_schedule.company.november.detail.length > 0 && (
                  <div className="mb-2">
                    <div className="text-xs text-muted-foreground uppercase font-medium mb-1">Societa</div>
                    {data.f24_schedule.company.november.detail.map((d, i) => (
                      <div key={i} className="flex justify-between text-sm py-0.5">
                        <span>{d.label}</span>
                        <span className="font-medium tabular-nums">{formatEur(d.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {(data.f24_schedule.partners || []).map((p, pi) => (
                  <div key={pi} className="mb-2">
                    <div className="text-xs text-muted-foreground uppercase font-medium mb-1">{p.name}</div>
                    {p.november.detail.map((d, i) => (
                      <div key={i} className="flex justify-between text-sm py-0.5">
                        <span>{d.label}</span>
                        <span className="font-medium tabular-nums">{formatEur(d.amount)}</span>
                      </div>
                    ))}
                  </div>
                ))}
                <div className="flex justify-between font-bold text-red-900 border-t pt-1 mt-1">
                  <span>Totale Novembre</span>
                  <span className="tabular-nums" data-testid="f24-nov-total">
                    {formatEur((data.f24_schedule.company.november.total || 0) + (data.f24_schedule.partners || []).reduce((s, p) => s + p.november.total, 0))}
                  </span>
                </div>
              </div>
            </div>

            {data.f24_schedule.rateizzazione && data.f24_schedule.rateizzazione.available && (
              <div className="mt-4 border rounded-lg p-4 bg-blue-50/30">
                <div className="font-semibold text-blue-800 mb-2">
                  Rateizzazione {data.f24_schedule.first_acconto_month || 'Luglio'} ({data.f24_schedule.rateizzazione.installments.length} rate mensili {data.f24_schedule.rateizzazione.installments[0]?.month_name?.slice(0,3) || 'Lug'}-{data.f24_schedule.rateizzazione.installments[data.f24_schedule.rateizzazione.installments.length - 1]?.month_name?.slice(0,3) || 'Dic'})
                </div>
                <div className="grid grid-cols-6 gap-2">
                  {data.f24_schedule.rateizzazione.installments.map((inst, i) => (
                    <div key={i} className="text-center border rounded p-2 bg-white">
                      <div className="text-xs text-muted-foreground">{inst.month_name}</div>
                      <div className="font-bold text-sm tabular-nums">{formatEur(inst.total)}</div>
                      {inst.interest > 0 && <div className="text-[10px] text-muted-foreground">+{formatEur(inst.interest)} int.</div>}
                    </div>
                  ))}
                </div>
                <div className="text-xs text-muted-foreground mt-2 text-center">
                  Totale con interessi: {formatEur(data.f24_schedule.rateizzazione.total_with_interest)} (interessi: {formatEur(data.f24_schedule.rateizzazione.total_interest)})
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Partners Detail */}
      {isPersonal && data.partners?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-violet-600" />Dettaglio per Socio
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-2 font-medium">Socio</th>
                    <th className="text-right py-2 font-medium">Quota %</th>
                    <th className="text-right py-2 font-medium">Reddito Impresa</th>
                    <th className="text-right py-2 font-medium">Altri Redditi</th>
                    <th className="text-right py-2 font-medium" title="IVS effettivamente versata durante l'anno (cassa) e dedotta dall'IRPEF">IVS Versata</th>
                    <th className="text-right py-2 font-medium">Imponibile</th>
                    <th className="text-right py-2 font-medium">IRPEF</th>
                    <th className="text-right py-2 font-medium">Aliq. Eff.</th>
                    <th className="text-right py-2 font-medium">IVS Dovuta</th>
                    <th className="text-right py-2 font-medium font-bold">Totale</th>
                  </tr>
                </thead>
                <tbody>
                  {data.partners.map((p, i) => (
                    <tr key={i} className="border-b hover:bg-slate-50">
                      <td className="py-2 font-medium">{p.name}</td>
                      <td className="text-right tabular-nums">{p.share_pct}%</td>
                      <td className="text-right tabular-nums">{formatEur(p.company_share)}</td>
                      <td className="text-right tabular-nums">{formatEur(p.other_income)}</td>
                      <td className="text-right tabular-nums text-emerald-700" data-testid={`partner-ivs-paid-${i}`}>
                        {p.ivs_already_paid > 0 ? `-${formatEur(p.ivs_deducted_from_irpef)}` : formatEur(0)}
                      </td>
                      <td className="text-right tabular-nums font-medium">{formatEur(p.taxable_income)}</td>
                      <td className="text-right tabular-nums text-red-600">{formatEur(p.irpef.net_tax)}</td>
                      <td className="text-right tabular-nums text-muted-foreground">{p.irpef.effective_rate}%</td>
                      <td className="text-right tabular-nums text-purple-600">{formatEur(p.ivs.total)}</td>
                      <td className="text-right tabular-nums font-bold text-red-700">{formatEur(p.total_tax)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.partners.some(p => p.double_deduction_warning) && (
                <div className="mt-3 flex items-start gap-2 p-3 bg-amber-50 border border-amber-300 rounded-lg" data-testid="double-deduction-warning">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-amber-900">
                    <strong>Possibile doppia deduzione IVS rilevata.</strong> Per uno o più soci il valore in "Deduzioni" è molto vicino all'IVS già versata. I contributi previdenziali versati vanno indicati come <em>acconti IVS</em> nel pannello acconti — non come deduzioni nel profilo socio. Lasciandoli in entrambe le voci stai deducendo due volte lo stesso importo dall'IRPEF.
                  </div>
                </div>
              )}
            </div>
            {data.partners[0]?.irpef?.brackets?.length > 0 && (
              <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                <div className="text-xs font-medium text-muted-foreground mb-2">Scaglioni IRPEF 2025 ({data.partners[0].name})</div>
                <div className="space-y-1">
                  {data.partners[0].irpef.brackets.map((b, i) => (
                    <div key={i} className="flex justify-between text-xs">
                      <span>{formatEur(b.from)} → {formatEur(b.to)}</span>
                      <span className="text-muted-foreground">{b.rate}%</span>
                      <span className="font-medium tabular-nums">{formatEur(b.tax)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Monthly Trend */}
      {barData.length > 1 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-indigo-600" />Andamento Mensile
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => formatEur(v)} />
                <Bar dataKey="EBITDA" fill="#10b981" radius={[4, 4, 0, 0]} name="EBITDA" />
                <Bar dataKey="Risultato" fill="#2563eb" radius={[4, 4, 0, 0]} name="Risultato A.I." />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* SRL Simulation (only for non-SRL: SNC, ditta, professionista handled above) */}
      {data.business_type !== 'srl' && data.simulazione_srl && (
        <SrlSimulationSection sim={data.simulazione_srl} simParams={simParams} setSimParams={setSimParams} onSave={saveSimParams} saving={simSaving} />
      )}

      {/* Partners Dialog */}
      <Dialog open={showPartners} onOpenChange={setShowPartners}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Gestisci Soci / Titolare</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {partners.map((p, i) => (
              <div key={i} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Input className="flex-1" placeholder="Nome socio" value={p.name || ""} onChange={e => {
                    const np = [...partners]; np[i] = { ...np[i], name: e.target.value }; setPartners(np);
                  }} data-testid={`partner-name-${i}`} />
                  <Input className="w-20" type="number" placeholder="%" value={p.share_pct || ""} onChange={e => {
                    const np = [...partners]; np[i] = { ...np[i], share_pct: parseFloat(e.target.value) || 0 }; setPartners(np);
                  }} data-testid={`partner-share-${i}`} />
                  <span className="text-sm text-muted-foreground">%</span>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => setPartners(partners.filter((_, j) => j !== i))}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Altri Redditi</Label>
                    <Input type="number" placeholder="0" value={p.other_income || ""} onChange={e => {
                      const np = [...partners]; np[i] = { ...np[i], other_income: parseFloat(e.target.value) || 0 }; setPartners(np);
                    }} />
                  </div>
                  <div>
                    <Label className="text-xs">Deduzioni</Label>
                    <Input type="number" placeholder="0" value={p.deductions || ""} onChange={e => {
                      const np = [...partners]; np[i] = { ...np[i], deductions: parseFloat(e.target.value) || 0 }; setPartners(np);
                    }} />
                  </div>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" className="w-full" onClick={() => setPartners([...partners, { name: "", share_pct: 0, other_income: 0, deductions: 0 }])}>
              <Plus className="h-4 w-4 mr-1" />Aggiungi Socio
            </Button>
            <div className="text-xs text-muted-foreground text-center">
              Totale quote: {partners.reduce((s, p) => s + (parseFloat(p.share_pct) || 0), 0)}%
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPartners(false)}>Annulla</Button>
            <Button onClick={savePartners} disabled={saving} data-testid="save-partners-btn">
              <Save className="h-4 w-4 mr-1" />{saving ? "Salvataggio..." : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Advances Dialog */}
      <Dialog open={showAdvances} onOpenChange={setShowAdvances}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Acconti Versati - Utile {year}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <p className="text-sm text-muted-foreground">
              Inserisci gli acconti di imposta e contributi gia versati nel {year + 1} relativi all'utile {year}. Verranno sottratti dal saldo dovuto negli F24 del {year + 1}.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2" data-testid="import-prev-year-block">
              <PiggyBank className="h-4 w-4 text-blue-700 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs font-semibold text-blue-900">Importa automaticamente da Pianificazione {year - 1}</p>
                <p className="text-[11px] text-blue-800 mt-0.5">Calcola gli acconti versati nel {year - 1} (= 100% imposta {year - 1}) e li registra qui. Sovrascrive i valori attuali.</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="bg-white text-blue-700 border-blue-300 hover:bg-blue-100 h-7 text-xs"
                onClick={async () => {
                  try {
                    const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
                    const res = await axios.post(`${API}/clients/${clientId}/tax-advances/import-previous-year`, { year, overwrite: true }, { headers });
                    if (res.data?.success) {
                      toast.success(res.data.message);
                      // Reload advances
                      const reload = await axios.get(`${API}/clients/${clientId}/tax-advances?year=${year}`, { headers });
                      const newAdv = {};
                      (reload.data || []).forEach(a => {
                        if (a.partner_name) {
                          newAdv[a.partner_name] = newAdv[a.partner_name] || {};
                          newAdv[a.partner_name][a.advance_type] = a.amount;
                        } else {
                          newAdv[a.advance_type] = a.amount;
                        }
                      });
                      setAdvances(newAdv);
                    } else {
                      toast.error(res.data?.message || "Importazione fallita");
                    }
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Errore importazione");
                  }
                }}
                data-testid="import-prev-year-btn"
              >
                Importa da {year - 1}
              </Button>
            </div>
            {!isPersonal && (
              <div className="space-y-2">
                <h4 className="font-semibold text-sm">Imposte Societarie</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Acconto IRES versato</Label>
                    <Input type="number" min="0" step="0.01" value={advances.ires || ''} onChange={e => setAdvances(p => ({ ...p, ires: parseFloat(e.target.value) || 0 }))} data-testid="advance-ires" />
                  </div>
                  <div>
                    <Label className="text-xs">Acconto IRAP versato</Label>
                    <Input type="number" min="0" step="0.01" value={advances.irap || ''} onChange={e => setAdvances(p => ({ ...p, irap: parseFloat(e.target.value) || 0 }))} data-testid="advance-irap" />
                  </div>
                </div>
              </div>
            )}
            {isPersonal && (
              <>
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm">IRAP Aziendale</h4>
                  <div>
                    <Label className="text-xs">Acconto IRAP versato</Label>
                    <Input type="number" min="0" step="0.01" value={advances.irap || ''} onChange={e => setAdvances(p => ({ ...p, irap: parseFloat(e.target.value) || 0 }))} data-testid="advance-irap" />
                  </div>
                </div>
                {data.partners?.map(partner => (
                  <div key={partner.name} className="space-y-2 p-3 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold text-sm">{partner.name} ({partner.share_pct}%)</h4>
                    {partner.double_deduction_warning && (
                      <div className="bg-amber-50 border border-amber-300 rounded p-2 text-xs text-amber-800">
                        ⚠️ I contributi previdenziali ({(partner.ivs_already_paid || 0).toLocaleString('it-IT')} €) sono dichiarati sia come "Deduzioni socio" sia come "Acconto IVS versato".
                        Lasciali SOLO nel campo "Acconto IVS versato": il sistema li dedurrà automaticamente da IRPEF.
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Acconto IRPEF versato</Label>
                        <Input type="number" min="0" step="0.01" value={advances[`${partner.name}_irpef`] || ''} onChange={e => setAdvances(p => ({ ...p, [`${partner.name}_irpef`]: parseFloat(e.target.value) || 0 }))} data-testid={`advance-irpef-${partner.name}`} />
                      </div>
                      <div>
                        <Label className="text-xs">Acconto IVS versato (incl. minimale)</Label>
                        <Input type="number" min="0" step="0.01" value={advances[`${partner.name}_ivs`] || ''} onChange={e => setAdvances(p => ({ ...p, [`${partner.name}_ivs`]: parseFloat(e.target.value) || 0 }))} data-testid={`advance-ivs-${partner.name}`} />
                        <p className="text-[10px] text-slate-500 mt-1">Includi tutti i contributi previdenziali versati (4 rate fisse + saldi/acconti).</p>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdvances(false)}>Annulla</Button>
            <Button onClick={saveAdvances} disabled={saving} data-testid="save-advances-btn">
              <Save className="h-4 w-4 mr-1" />{saving ? "Salvataggio..." : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaxPlanningPage;
